/* mm-bundle-picker: the Bundle & Save picker in the PDP buy box (sections/mm-pdp-buy-box.liquid).
   The logic is ported from the live theme's assets/mm-bundle.js (class BundlePicker, pulled 2026-10-21); only the
   markup is new (spec §2 tier bar + bag picker card, §5.1):
   - Tier lookup: MaisonEngine.config().tiers[data-product-id]. No tier → the element stays hidden.
   - cards(): titles from the engine config cards; totals = tierLocalCents(tier, qty, singles), singles = page variant
     price × qty, qty 1 = the page variant's price. Design v5 (2026-10-23): the struck price is compare-at × bags (the
     variant's compare-at, or its price when it has none), as the design and live; the qty 1 row strikes compare-at.
   - Tag pill per tier: data-tag-N (section setting) or the engine card's badge / label; data-popular = the blush one.
   - Tier note: data-note-N (section setting, e.g. "15% off (Additional Savings)") or, when blank, computed:
     data-t-tier-save ("Save {amount}", amount = compare − tier total) + " · " + data-t-tier-ship ("free shipping") when
     the tier total reaches the free-shipping minimum for this currency: data-ship (cents) or, when blank,
     data-ship-usd × Shopify's rate rounded up to the next 5, exactly as the cart bar (snippets/mm-rewards-config).
   - The picked tier opens its bag tray (one bag card per bag + 34px swatches), the 1-bag tier too: there a colour
     tap changes the page's variant ('mm:bundle:single' → <mm-pdp>.selectVariant), so the payload stays the page's
     variant. data-default = the tier picked on load.
   - Picks / slots: one colour per bag; a new bag copies the previous bag's colour; tapping a colour sets the active
     bag and moves on to the next one.
   - add(btn, err, times): the picks counted per variant → MaisonEngine.addItems([{ id, quantity × times }, ...])
     (times = the buy box quantity stepper, default 1). NO properties.
   Design additions (display only, same maths): on the top tier "+ Add a bag" and ✕ remove change the bag count;
   above the top tier tierLocalCents → tierTotal carries the top tier's per-bag price on, exactly like the engine.
   One fix vs live: while 1 bag is selected the first pick follows the page's colour, so a 2+ bundle starts with the
   colour the shopper is looking at (live started from the colour the page loaded with). Payload unchanged.
   API for assets/mm-pdp.js (ATC label, sticky bar, colour card): active, tier, qty, activeBag, picks, variant(id),
   summary(times), setQty(), setSlot(), pick(), addBag(), removeBag(), items(times), add(button, errorEl, times).
   Fires 'mm:bundle:change' (bubbles) after every render. Listens for 'mm:variant:change' on its section. */
(() => {
  const E = () => window.MaisonEngine;
  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const fill = (tpl, map) => String(tpl || '').replace(/\{(\w+)\}/g, (m, k) => (k in map ? map[k] : m));
  const sized = (src, w) => (src ? `${src}${src.includes('?') ? '&' : '?'}width=${w}` : '');
  const clean = (name) => String(name || '').replace(/\s*\(.*?\)\s*/g, ' ').replace(/\s+/g, ' ').trim();
  const INFO = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 11v5.5M12 7.6v.2"/></svg>';
  const CROSS = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="m7 7 10 10M17 7 7 17"/></svg>';
  // Free-shipping minimum for currencies without their own amount: USD × Shopify's rate rounded UP to the next 5 (cart bar rule).
  const up5 = (usd) => Math.ceil((Number(usd) * Number(window.Shopify?.currency?.rate || 1)) / 5) * 5 * 100;

  class BundlePicker extends HTMLElement {
    connectedCallback() {
      if (!this.ready) this.setup();
      if (!this.tier) return;
      this.scope.addEventListener('mm:variant:change', this.onVariant);
    }
    disconnectedCallback() { this.scope?.removeEventListener('mm:variant:change', this.onVariant); }

    setup() {
      this.ready = true;
      this.tier = E()?.config?.()?.tiers?.[this.dataset.productId];
      if (!this.tier) { this.hidden = true; return; } // no bundle deal for this product: stay hidden
      this.scope = this.closest('.shopify-section, section') || document;
      this.product = JSON.parse((this.scope.querySelector('script[data-mm-product]') || this.querySelector('script[data-mm-product]')).textContent);
      this.body = this.querySelector('[data-picker-body]') || this;
      this.qty = 1;
      this.picks = [];
      const def = Number(this.dataset.default) || 1;
      this.activeBag = 0;
      this.max = Math.max(Number(this.dataset.max) || 10, this.topQty());
      this.qty = Math.max(1, Math.min(def, this.max));
      this.ship = this.dataset.ship ? Number(this.dataset.ship) || 0 : this.dataset.shipUsd ? up5(this.dataset.shipUsd) : 0;
      this.onVariant = () => { if (this.qty === 1) { this.picks = [this.pageVariant().id]; this.render(); } };
      this.closest('mm-pdp')?.classList.add('has-picker');
      this.addEventListener('change', (e) => { const r = e.target.closest?.('input[data-tier]'); if (r) this.setQty(Number(r.value)); });
      this.addEventListener('click', (e) => this.onClick(e));
      this.render();
      this.hidden = false;
    }

    get active() { return !!this.tier; }

    // The page's selected variant (the buy box's hidden id input), as live.
    pageVariant() {
      const id = Number([...this.scope.querySelectorAll('[name="id"]')].find((i) => !i.disabled && i.value)?.value);
      return this.product.variants.find((v) => v.id === id) || this.product.variants.find((v) => v.available) || this.product.variants[0];
    }
    variant(id) { return this.product.variants.find((x) => x.id === id) || this.pageVariant(); }
    colourName(v) { return clean(v.option1 || v.title); }

    // Live cards(): engine config cards (or 1 + listed quantities), priced from the page variant (totals()).
    cards() {
      const fromConfig = this.tier.cards?.length ? this.tier.cards : [1, ...Object.keys(this.tier.totals).map(Number)].map((qty) => ({ qty, title: `${qty} ${this.tier.name}` }));
      return fromConfig.map((card) => ({ ...card, ...this.totals(Number(card.qty)) }));
    }
    topQty() {
      const qs = this.tier.cards?.length ? this.tier.cards.map((c) => Number(c.qty)) : [1, ...Object.keys(this.tier.totals).map(Number)];
      return Math.max(...qs);
    }
    // Engine total for any bag count (above the top tier the engine keeps the top tier's per-bag price) and what the
    // same bags cost bought one at a time today (singles = the engine's singlesCents): the struck price and the saving.
    totals(qty) {
      const v = this.pageVariant();
      const singles = v.price * qty;
      const total = qty === 1 ? v.price : (E().tierLocalCents(this.tier, qty, singles) ?? singles);
      return { total, compare: Math.max(v.compare_at_price || 0, v.price) * qty };
    }
    // "Save $9.95 · free shipping": the saving against singles, plus free shipping once the total reaches the minimum.
    note(total, compare) {
      const d = this.dataset;
      const parts = [];
      if (compare > total && d.tTierSave) parts.push(fill(d.tTierSave, { amount: E().money(compare - total) }));
      if (this.ship > 0 && total >= this.ship && d.tTierShip) parts.push(d.tTierShip);
      const text = parts.join(' · ');
      return text.charAt(0).toUpperCase() + text.slice(1);
    }
    // times = the buy box quantity stepper: that many of this bundle, priced by the engine for all those bags.
    summary(times = 1) {
      const n = this.qty * Math.max(1, times);
      const { total, compare } = this.totals(n);
      return { qty: n, total, compare, perUnit: Math.round(total / n), picks: this.picks.map((id) => this.variant(id)), activeBag: this.activeBag };
    }

    // Live render() bookkeeping: a new bag copies the previous bag's colour (or the page's), extra picks drop off.
    syncPicks() {
      const v = this.pageVariant();
      while (this.picks.length < this.qty) this.picks.push(this.picks[this.picks.length - 1] ?? v.id);
      this.picks.length = this.qty;
      this.activeBag = Math.min(this.activeBag ?? 0, this.qty - 1);
    }

    setQty(qty) {
      this.qty = Math.max(1, Math.min(Number(qty) || 1, this.max));
      if (this.qty === 1) this.picks = [this.pageVariant().id];
      this.render();
    }
    setSlot(i) { this.activeBag = Math.max(0, Math.min(Number(i) || 0, this.qty - 1)); this.render(); }
    pick(id) {
      if (this.qty === 1) { this.dispatchEvent(new CustomEvent('mm:bundle:single', { bubbles: true, detail: { id: Number(id) } })); return; }
      this.picks[this.activeBag] = Number(id);
      if (this.activeBag < this.qty - 1) this.activeBag += 1;
      this.render();
    }
    addBag() {
      if (this.qty < this.topQty() || this.qty >= this.max) return;
      this.qty += 1;
      this.syncPicks();
      this.activeBag = this.qty - 1;
      this.render();
    }
    removeBag(i) {
      if (this.qty <= this.topQty()) return;
      this.picks.splice(i, 1);
      this.qty -= 1;
      if (i < this.activeBag) this.activeBag -= 1;
      this.activeBag = Math.min(this.activeBag, this.qty - 1);
      this.render();
    }

    // Live add(): one line per chosen colour, quantity = bags of that colour. No properties.
    items(times = 1) {
      const counts = new Map();
      for (const id of this.picks) counts.set(id, (counts.get(id) || 0) + 1);
      return [...counts].map(([id, quantity]) => ({ id, quantity: quantity * Math.max(1, times) }));
    }
    async add(btn, err, times = 1) {
      if (btn?.getAttribute('aria-busy') === 'true') return false;
      btn?.setAttribute('aria-busy', 'true');
      if (err) err.hidden = true;
      try { await E().addItems(this.items(times)); return true; }
      catch (e) { if (err) { err.textContent = e.message || this.dataset.tError; err.hidden = false; } return false; }
      finally { btn?.removeAttribute('aria-busy'); }
    }

    onClick(e) {
      const t = e.target;
      const rm = t.closest('[data-remove]');
      if (rm) { this.removeBag(Number(rm.dataset.remove)); return; }
      const slot = t.closest('[data-slot]');
      if (slot) { this.setSlot(Number(slot.dataset.slot)); return; }
      const colour = t.closest('[data-colour]');
      if (colour && !colour.disabled) { this.pick(Number(colour.dataset.colour)); return; }
      if (t.closest('[data-add-bag]')) this.addBag();
    }

    bg(v) {
      const named = E()?.swatchBg?.(v.option1 || v.title);
      if (named) return named;
      const src = v.featured_image?.src;
      return src ? `url('${sized(src, 96)}')` : 'var(--mm-sand-200)';
    }
    units() {
      const d = this.dataset;
      const unit = d.unit || 'bag';
      return { unit, units: d.units || `${unit}s`, Unit: unit.charAt(0).toUpperCase() + unit.slice(1), a_unit: `${/^[aeiou]/i.test(unit) ? 'an' : 'a'} ${unit}` };
    }
    preLine(v) {
      const d = this.dataset;
      const ships = E().preNote(v);
      const shipsText = ships && ships !== 'Ships when ready' ? ships.replace(/^Ships/i, 'ships') : '';
      const head = shipsText ? fill(d.tPreHead, { colour: this.colourName(v), ships: shipsText }) : fill(d.tPreHeadPlain, { colour: this.colourName(v) });
      return `<p class="mm-pre-note mm-bp__pre-note"><strong>${esc(head)}</strong> ${esc(d.tPreNote)}</p>`;
    }

    trayHtml(u) {
      const d = this.dataset;
      const top = this.topQty();
      const removable = this.qty > top;
      const canAdd = this.qty >= top && this.qty < this.max;
      const active = this.variant(this.picks[this.activeBag]);
      const bags = this.picks.map((id, i) => {
        const v = this.variant(id);
        const label = fill(d.tBag, { ...u, n: i + 1 });
        const pre = E().isPre(v);
        const ships = E().preNote(v);
        const src = v.featured_image?.src;
        return `
          <div class="mm-bp__bag${i === this.activeBag ? ' is-active' : ''}">
            <button type="button" class="mm-bp__bag-btn" role="tab" aria-selected="${i === this.activeBag}" data-slot="${i}" aria-label="${esc(label)}: ${esc(this.colourName(v))}">
              <span class="mm-bp__bag-media">${src ? `<img src="${esc(sized(src, 240))}" alt="" width="120" height="120" loading="lazy">` : ''}<span class="mm-bp__bag-dot" style="--swatch:${esc(this.bg(v))}"></span></span>
              <span class="mm-bp__bag-label">${esc(label)}</span>
              <span class="mm-bp__bag-colour">${esc(this.colourName(v))}</span>
              ${pre ? `<span class="mm-pre" title="${esc(ships || '')}">${esc(d.tPre)}${INFO}</span>` : ''}
            </button>
            ${removable ? `<button type="button" class="mm-bp__remove" data-remove="${i}" aria-label="${esc(fill(d.tRemove, { ...u, n: i + 1 }))}">✕</button>` : ''}
          </div>`;
      }).join('');
      const add = canAdd ? `<button type="button" class="mm-bp__add" data-add-bag>+ ${esc(fill(d.tAdd, u))}</button>` : '';
      const colourOf = fill(d.tColourOf, { ...u, n: this.activeBag + 1 });
      const swatches = this.product.variants.map((x) => {
        const on = x.id === active.id;
        return `<button type="button" class="mm-bp__sw${on ? ' is-on' : ''}" role="radio" aria-checked="${on}" data-colour="${x.id}" aria-label="${esc(this.colourName(x))}${x.available ? '' : ` (${esc(d.tSoldout)})`}" title="${esc(this.colourName(x))}" style="--swatch:${esc(this.bg(x))}"${x.available ? '' : ' disabled'}></button>`;
      }).join('');
      const seen = new Set();
      const notes = this.picks.map((id) => this.variant(id)).filter((v) => E().isPre(v) && !seen.has(v.id) && seen.add(v.id)).map((v) => this.preLine(v)).join('');
      return `
        <div class="mm-bp__tray">
          <p class="mm-bp__tray-head"><span class="mm-bp__tray-title">${esc(fill(d.tBundle, { ...u, count: this.qty }))}</span><span class="mm-bp__hint">${esc(fill(d.tHint, u))}</span></p>
          <div class="mm-bp__bags" role="tablist" aria-label="${esc(fill(d.tBundle, { ...u, count: this.qty }))}">${bags}${add}</div>
          ${notes}
          <div class="mm-bp__swatches" role="radiogroup" aria-label="${esc(colourOf)}">${swatches}</div>
        </div>`;
    }

    render() {
      this.syncPicks();
      const d = this.dataset;
      const u = this.units();
      const money = E().money;
      const cards = this.cards();
      const sel = cards.filter((c) => c.qty <= this.qty).pop() || cards[0];
      const multi = this.product.variants.length > 1;
      // Keep keyboard focus on the same control across the re-render.
      const a = document.activeElement;
      let focusKey = null;
      if (a && this.contains(a)) {
        if (a.dataset.slot != null) focusKey = `[data-slot="${a.dataset.slot}"]`;
        else if (a.dataset.colour) focusKey = `[data-colour="${a.dataset.colour}"]`;
        else if (a.matches('input[data-tier]')) focusKey = 'input[data-tier]:checked';
        else if (a.matches('[data-add-bag]')) focusKey = '[data-add-bag]';
        else if (a.dataset.remove != null) focusKey = '[data-slot]';
      }
      const rows = cards.map((c, k) => {
        const on = c === sel;
        const q = on ? this.qty : c.qty;
        const t = on ? this.totals(this.qty) : { total: c.total, compare: c.compare };
        const n = k + 1;
        const note = this.getAttribute(`data-note-${n}`) || this.note(t.total, t.compare);
        const tag = this.getAttribute(`data-tag-${n}`) || c.badge || c.label || '';
        return `
          <div class="mm-bp__tier${on ? ' is-on' : ''}">
            ${tag ? `<span class="mm-bp__tag${String(n) === d.popular ? ' is-blush' : ''}">${esc(tag)}</span>` : ''}
            <label class="mm-bp__row">
              <input class="mm-bp__input" type="radio" name="mm-bp-${esc(d.productId)}" value="${c.qty}" data-tier${on ? ' checked' : ''}>
              <span class="mm-bp__radio" aria-hidden="true"></span>
              <span class="mm-bp__text">
                <span class="mm-bp__title">${esc(c.title)}</span>
                ${note ? `<span class="mm-bp__note" data-tier-note>${esc(note)}</span>` : ''}
              </span>
              <span class="mm-bp__price"><strong>${money(t.total)}</strong>${t.compare > t.total ? `<s><span class="mm-visually-hidden">${esc(d.tRegular)}</span>${money(t.compare)}</s>` : ''}${q > 1 ? `<small>${money(Math.round(t.total / q))} ${esc(fill(d.tPer, u))}</small>` : ''}</span>
            </label>
            ${on && multi ? this.trayHtml(u) : ''}
          </div>`;
      }).join('');
      this.body.innerHTML = `
        <p class="mm-bp__heading"><span>${esc(d.tHeading)}</span></p>
        <div class="mm-bp__tiers" role="radiogroup" aria-label="${esc(d.tHeading)}">${rows}</div>`;
      if (focusKey) this.querySelector(focusKey)?.focus({ preventScroll: true });
      this.dispatchEvent(new CustomEvent('mm:bundle:change', { bubbles: true, detail: this.summary() }));
    }
  }
  if (!customElements.get('mm-bundle-picker')) customElements.define('mm-bundle-picker', BundlePicker);
})();
